import React, { ComponentProps, useState } from 'react';
import { DefaultView, OptionalEditView, Views } from '../../types/BlockWithViews';
import { AttachedFilesEditView } from './views/AttachedFilesEditView';
import { asFetchableBlock, useRefreshBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { Icons } from '../../components/atoms/SVG';
import { AttachedFilesDefaultView } from './views/AttachedFilesDefaultView';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { BlockBar, BlockBarAnchor, useBlockBarCreateHref } from '../../_lib/components/BlockBar';
import { useHtmlValidId } from '../../hooks/useHtmlValidId';
import { useAppContext } from '../../contexts/AppContext/AppContext';

type AttachedFilesProps = Views<DefaultView<AttachedFilesDefaultView> & OptionalEditView<AttachedFilesEditView>>;

function AttachedFiles({ activeView = 'default', views }: AttachedFilesProps) {
  const { t } = useSonTranslation('AttachedFiles');
  const { editModeInfo } = useAppContext();
  const id = useHtmlValidId();
  const { refresh } = useRefreshBlock();
  const [currentActiveView, setCurrentActiveView] = useState(activeView);
  const currentActiveViewHref = useBlockBarCreateHref(
    currentActiveView,
    { default: views.edit?.href, edit: views.default?.href },
    id,
  );

  const getActiveView = () => {
    if (currentActiveView === 'default') {
      return <AttachedFilesDefaultView {...views.default} />;
    }

    if (currentActiveView === 'edit') {
      return <AttachedFilesEditView {...(views.edit as AttachedFilesEditView)} />;
    }

    return null;
  };

  return (
    <>
      {editModeInfo.active
        ? <BlockBar heading={t('headline')} hierarchy={3}>
            <BlockBarAnchor
              href={currentActiveViewHref}
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                event.stopPropagation();
                event.preventDefault();

                if (currentActiveView === 'default') {
                  setCurrentActiveView('edit');
                } else if (currentActiveView === 'edit') {
                  setCurrentActiveView('default');
                  refresh();
                }
              }}
              text={t('blockInteractions.edit.text')}
              svgSpriteId={currentActiveView === 'edit' ? Icons.DECLINE_CROSS : Icons.EDIT_PENCIL}
              aria-controls={id}
              aria-expanded={currentActiveView === 'edit'}
            />
          </BlockBar>
        : null}
      <div id={id}>{getActiveView()}</div>
    </>
  );
}

export const AttachedFilesBlock = asFetchableBlock(AttachedFiles, BlockType.attachedfiles);
export type AttachedFilesBlock = ComponentProps<typeof AttachedFilesBlock>;
